import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Copy, Save, Sparkles, X } from "lucide-react";

interface Event {
  id: string;
  name: string;
  event_date: string;
}

interface Goal {
  id: string;
  goal_name: string;
}

interface PTAData {
  name: string;
  logo_url: string | null;
}

const DEFAULT_TEMPLATES = [
  {
    id: "donation",
    name: "Donation Request",
    goal: "donation request",
    description: "Request financial support for your event or cause"
  },
  {
    id: "sponsorship",
    name: "Sponsorship Request",
    goal: "sponsorship request",
    description: "Seek business sponsorship and partnership opportunities"
  },
  {
    id: "volunteer",
    name: "Volunteer Recruitment",
    goal: "volunteer recruitment",
    description: "Recruit volunteers to help with your event"
  },
  {
    id: "invitation",
    name: "Event Invitation",
    goal: "event invitation",
    description: "Formally invite recipients to your upcoming event"
  },
  {
    id: "thankyou",
    name: "Thank You Letter",
    goal: "thank you letter",
    description: "Express gratitude for past support and contributions"
  },
  {
    id: "stall",
    name: "Stall Space Invitation",
    goal: "stall space invitation",
    description: "Invite businesses to rent a stall at your event"
  }
];

interface LetterBuilderProps {
  ptaId: string;
}

export function LetterBuilder({ ptaId }: LetterBuilderProps) {
  const [events, setEvents] = useState<Event[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [ptaData, setPtaData] = useState<PTAData | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [pendingTemplate, setPendingTemplate] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("");
  const [selectedGoal, setSelectedGoal] = useState("");
  const [customLetterGoal, setCustomLetterGoal] = useState("");
  const [customInstructions, setCustomInstructions] = useState("");
  const [generatedLetter, setGeneratedLetter] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  useEffect(() => {
    if (ptaId) {
      fetchEvents();
      fetchGoals();
      fetchPTAData();
    }
  }, [ptaId]);

  const fetchEvents = async () => {
    const { data, error } = await supabase
      .from('events')
      .select('id, name, event_date')
      .eq('pta_id', ptaId)
      .order('event_date', { ascending: true });

    if (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
      return;
    }

    setEvents(data || []);
  };

  const fetchGoals = async () => {
    const { data, error } = await supabase
      .from('fund_allocations')
      .select('id, goal_name')
      .order('goal_name', { ascending: true });

    if (error) {
      console.error('Error fetching goals:', error);
      return;
    }

    setGoals(data || []);
  };

  const fetchPTAData = async () => {
    const { data, error } = await supabase
      .from('ptas')
      .select('name, logo_url')
      .eq('id', ptaId)
      .single();

    if (error) {
      console.error('Error fetching PTA data:', error);
      return;
    }

    setPtaData(data);
  };

  const handleTemplateSelect = (templateId: string) => {
    if (generatedLetter && selectedTemplate !== templateId) {
      setPendingTemplate(templateId);
      setShowSaveDialog(true);
    } else {
      setSelectedTemplate(templateId);
      const template = DEFAULT_TEMPLATES.find(t => t.id === templateId);
      if (template) {
        setTemplateName(template.name);
      }
    }
  };

  const handleConfirmTemplateChange = (saveFirst: boolean) => {
    if (saveFirst && generatedLetter && templateName) {
      handleSaveTemplate();
      // Keep the letter visible when saving
    } else {
      // Only clear if they chose not to save
      setGeneratedLetter("");
    }
    
    setSelectedTemplate(pendingTemplate);
    const template = DEFAULT_TEMPLATES.find(t => t.id === pendingTemplate);
    if (template) {
      setTemplateName(template.name);
    }
    setShowSaveDialog(false);
    setPendingTemplate("");
  };

  const handleGenerate = async () => {
    const template = selectedTemplate ? DEFAULT_TEMPLATES.find(t => t.id === selectedTemplate) : null;
    const event = selectedEvent ? events.find(e => e.id === selectedEvent) : null;
    const goal = selectedGoal ? goals.find(g => g.id === selectedGoal) : null;

    // Determine the goal: use template goal, or custom goal, or default
    const letterGoal = template?.goal || customLetterGoal || "general letter";

    if (!template && !customLetterGoal.trim()) {
      toast.error('Please select a template or describe what type of letter you want');
      return;
    }

    setIsGenerating(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-letter', {
        body: {
          recipientName: recipientName || undefined,
          eventName: event?.name || undefined,
          eventDate: event ? new Date(event.event_date).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }) : undefined,
          goal: letterGoal,
          customInstructions: customInstructions || undefined,
          ptaName: ptaData?.name || undefined,
          selectedGoal: goal?.goal_name || undefined
        }
      });

      if (error) throw error;

      // Aggressively strip any HTML tags and clean up the text
      let cleanLetter = data.letter
        .replace(/<[^>]*>/g, '') // Remove all HTML tags
        .replace(/&nbsp;/g, ' ') // Replace HTML spaces
        .replace(/&amp;/g, '&') // Replace HTML entities
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\n\s*\n\s*\n/g, '\n\n') // Normalize multiple blank lines to just one
        .trim();
      
      setGeneratedLetter(cleanLetter);
      toast.success('Letter generated! Edit as needed, then click Generate PDF when ready.');
    } catch (error) {
      console.error('Error generating letter:', error);
      toast.error('Failed to generate letter');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!generatedLetter) return;
    
    navigator.clipboard.writeText(generatedLetter);
    toast.success('Letter copied to clipboard!');
  };

  const handleSaveTemplate = async () => {
    if (!generatedLetter || !templateName) {
      toast.error('Please provide a template name');
      return;
    }

    try {
      const { error } = await supabase
        .from('community_letter_templates')
        .insert({
          pta_id: ptaId,
          name: templateName,
          type: selectedTemplate,
          content: generatedLetter
        });

      if (error) throw error;

      toast.success('Template saved successfully!');
      setTemplateName("");
      
      // Trigger refresh in parent component by dispatching custom event
      window.dispatchEvent(new CustomEvent('template-saved'));
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  return (
    <div className="space-y-6">
      <AlertDialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save Current Letter?</AlertDialogTitle>
            <AlertDialogDescription>
              You have a generated letter. Would you like to save it as a template before switching?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleConfirmTemplateChange(false)}>
              Don't Save
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => handleConfirmTemplateChange(true)}>
              Save First
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Select a Template (Optional)</CardTitle>
            <CardDescription>Choose from our pre-designed letter templates or create a custom letter below</CardDescription>
          </div>
          {selectedTemplate && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedTemplate("");
                setCustomLetterGoal("");
              }}
            >
              <X className="h-4 w-4 mr-1" />
              Clear Template
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {DEFAULT_TEMPLATES.map((template) => (
              <Card
                key={template.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedTemplate === template.id ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => handleTemplateSelect(template.id)}
              >
                <CardHeader>
                  <CardTitle className="text-lg">{template.name}</CardTitle>
                  <CardDescription className="text-sm">{template.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Customize Your Letter</CardTitle>
          <CardDescription>Fill in the details for your letter</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!selectedTemplate && (
            <div className="space-y-2">
              <Label htmlFor="customGoal">What type of letter do you need?</Label>
              <Input
                id="customGoal"
                placeholder="e.g., fundraising appeal, volunteer request, event announcement..."
                value={customLetterGoal}
                onChange={(e) => setCustomLetterGoal(e.target.value)}
              />
            </div>
          )}
            <div className="space-y-2">
              <Label htmlFor="recipient">Recipient Name (Optional)</Label>
              <Input
                id="recipient"
                placeholder="Leave blank for 'To Whom It May Concern'"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="event">Select Event (Optional)</Label>
              <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an event (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name} - {new Date(event.event_date).toLocaleDateString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="goal">Fundraising Goal (Optional)</Label>
              <Select value={selectedGoal} onValueChange={setSelectedGoal}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a fundraising goal (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {goals.map((goal) => (
                    <SelectItem key={goal.id} value={goal.id}>
                      {goal.goal_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="instructions">Additional Instructions (Optional)</Label>
              <Textarea
                id="instructions"
                placeholder="Add any specific details or requirements for the letter..."
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                rows={3}
              />
            </div>

            <Button 
              onClick={handleGenerate} 
              disabled={isGenerating}
              className="w-full"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating Letter...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Letter with AI
                </>
              )}
            </Button>
          </CardContent>
        </Card>

      {generatedLetter && (
        <Card>
          <CardHeader>
            <CardTitle>Your Letter</CardTitle>
            <CardDescription>Edit the letter as needed, then copy to use in Word or email</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={generatedLetter}
              onChange={(e) => setGeneratedLetter(e.target.value)}
              rows={12}
              className="text-base leading-relaxed"
              placeholder="Your letter content will appear here..."
            />

            <div className="flex flex-wrap gap-2">
              <Button onClick={handleCopy} variant="outline">
                <Copy className="mr-2 h-4 w-4" />
                Copy to Clipboard
              </Button>
              <div className="flex-1 flex gap-2">
                <div className="flex-1 space-y-1">
                  <Input
                    id="templateName"
                    placeholder="Enter template name..."
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Give your letter a name to save it as a template</p>
                </div>
                <Button onClick={handleSaveTemplate} disabled={!templateName} className="self-start">
                  <Save className="mr-2 h-4 w-4" />
                  Save as Template
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
